'use client';
import { jsx } from 'react/jsx-runtime';
import '@digdir/designsystemet-web';
import cl from 'clsx/lite';
import { createContext, forwardRef, useState, useRef, useId, useEffect } from 'react';
import { useMergeRefs } from '../../utilities/hooks/use-merge-refs/use-merge-refs.js';

const Context = createContext({});
/**
 * Display a group of tabs that can be toggled between.
 *
 * @example
 * <Tabs onChange={(value) => console.log(value)}>
 *   <Tabs.List>
 *     <Tabs.Tab value='1'>Tab 1</Tabs.Tab>
 *     <Tabs.Tab value='2'>Tab 2</Tabs.Tab>
 *     <Tabs.Tab value='3'>Tab 3</Tabs.Tab>
 *   </Tabs.List>
 *   <Tabs.Panel value='1'>content 1</Tabs.Panel>
 *   <Tabs.Panel value='2'>content 2</Tabs.Panel>
 *   <Tabs.Panel value='3'>content 3</Tabs.Panel>
 * </Tabs>
 */
const Tabs = forwardRef(function Tabs({ value, defaultValue, className, onChange, ...rest }, ref) {
    const isControlled = value !== undefined;
    const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue);
    const tabsRef = useRef(null);
    const valuePrefix = useId(); // Used to generate unique value-based ids for tabs and panels
    const mergedRefs = useMergeRefs([ref, tabsRef]);
    let onValueChange = onChange;
    if (!isControlled) {
        onValueChange = (newValue) => {
            setUncontrolledValue(newValue);
            onChange?.(newValue);
        };
        value = uncontrolledValue;
    }
    useEffect(() => {
        if (!isControlled || !tabsRef.current || value === undefined)
            return;
        tabsRef.current?.tabList?.tabs?.forEach((tab) => {
            if (tab.getAttribute('data-value') === value)
                tab.click();
        });
    }, [value, isControlled]);
    return (jsx(Context.Provider, { value: {
            isControlled,
            currentValue: value,
            defaultValue,
            onChange: onValueChange,
            getPrefixedValue: (value) => value && `${valuePrefix}-${value}`,
        }, children: jsx("ds-tabs", { suppressHydrationWarning // Since <ds-tablist> adds attributes
            : true, ref: mergedRefs, class: cl('ds-tabs', className), ...rest }) }));
});

export { Context, Tabs };
